import { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate, Location } from 'react-router-dom';

export interface NavigationBlocker {
	state: 'blocked' | 'unblocked' | 'proceeding';
	location: Location | null;
	proceed: () => void;
	reset: () => void;
}

interface ShouldBlockArgs {
	currentLocation: Location;
	nextLocation: Location;
}

/**
 * Custom navigation blocker hook that provides similar functionality to React Router's useBlocker
 * but works with BrowserRouter instead of requiring data router APIs.
 *
 * @param shouldBlock - Function that determines if navigation should be blocked
 * @returns NavigationBlocker object with state, location, proceed, and reset methods
 */
export function useNavigationBlocker(shouldBlock: (args: ShouldBlockArgs) => boolean): NavigationBlocker {
	const location = useLocation();
	const navigate = useNavigate();

	const [blockerState, setBlockerState] = useState<'blocked' | 'unblocked' | 'proceeding'>('unblocked');
	const [blockedLocation, setBlockedLocation] = useState<Location | null>(null);

	const currentLocationRef = useRef(location);
	const previousLocationRef = useRef(location);
	const shouldBlockRef = useRef(shouldBlock);
	const isNavigatingRef = useRef(false);
	const lastBlockedPathRef = useRef<string | null>(null);

	// Update shouldBlock ref
	useEffect(() => {
		shouldBlockRef.current = shouldBlock;
	}, [shouldBlock]);

	const proceed = useCallback(() => {
		if (blockedLocation && blockerState === 'blocked') {
			setBlockerState('proceeding');
			isNavigatingRef.current = true;

			// Navigate to the blocked location
			setTimeout(() => {
				navigate(blockedLocation.pathname + blockedLocation.search + blockedLocation.hash, {
					state: blockedLocation.state,
				});
				setBlockerState('unblocked');
				setBlockedLocation(null);
				isNavigatingRef.current = false;
			}, 0);
		}
	}, [blockedLocation, blockerState, navigate]);

	const reset = useCallback(() => {
		setBlockerState('unblocked');
		setBlockedLocation(null);
		lastBlockedPathRef.current = null;
	}, []);

	// Handle browser back/forward buttons and page refresh
	useEffect(() => {
		// Handle beforeunload (page refresh, close, etc.)
		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			const shouldBlockNavigation = shouldBlockRef.current({
				currentLocation: currentLocationRef.current,
				nextLocation: currentLocationRef.current, // Same location for refresh
			});

			if (shouldBlockNavigation && !isNavigatingRef.current) {
				event.preventDefault();
				event.returnValue = ''; // Chrome requires returnValue to be set
			}
		};

		// Handle popstate (browser back/forward buttons)
		const handlePopState = (event: PopStateEvent) => {
			if (isNavigatingRef.current) {
				return;
			}

			// Create a location object from the current window location
			const nextLocation: Location = {
				pathname: window.location.pathname,
				search: window.location.search,
				hash: window.location.hash,
				state: event.state,
				key: 'default',
			};

			const shouldBlockNavigation = shouldBlockRef.current({
				currentLocation: currentLocationRef.current,
				nextLocation,
			});

			if (shouldBlockNavigation) {
				// Prevent the navigation by pushing the current location back
				window.history.pushState(
					currentLocationRef.current.state,
					'',
					currentLocationRef.current.pathname +
						currentLocationRef.current.search +
						currentLocationRef.current.hash,
				);

				// Set blocked state
				setBlockerState('blocked');
				setBlockedLocation(nextLocation);
			}
		};

		window.addEventListener('beforeunload', handleBeforeUnload);
		window.addEventListener('popstate', handlePopState);

		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
			window.removeEventListener('popstate', handlePopState);
		};
	}, []);

	// Detect React Router navigation changes and block if necessary
	// Use useLayoutEffect to run synchronously before browser paint, reducing flicker
	useLayoutEffect(() => {
		// Use window.location.search if React Router's location doesn't have query params
		const actualSearch = location.search || window.location.search;
		const actualHash = location.hash || window.location.hash;

		// Skip if we're currently proceeding with navigation
		if (isNavigatingRef.current || blockerState === 'proceeding') {
			// Still update the refs
			previousLocationRef.current = location;
			currentLocationRef.current = location;
			return;
		}

		// If we're already in blocked state and location somehow changed, reset
		if (blockerState === 'blocked') {
			setBlockerState('unblocked');
			setBlockedLocation(null);
			lastBlockedPathRef.current = null;
			previousLocationRef.current = location;
			currentLocationRef.current = location;
			return;
		}

		// Check if location actually changed
		if (previousLocationRef.current.pathname === location.pathname) {
			// Still update previousLocationRef with actual search params
			previousLocationRef.current = {
				...location,
				search: actualSearch,
				hash: actualHash,
			};
			currentLocationRef.current = {
				...location,
				search: actualSearch,
				hash: actualHash,
			};
			return;
		}

		// Create location objects with actual search/hash from window if needed
		const currentLocationWithParams = {
			...previousLocationRef.current,
			search: previousLocationRef.current.search || window.location.search,
			hash: previousLocationRef.current.hash || window.location.hash,
		};

		const nextLocationWithParams = {
			...location,
			search: actualSearch,
			hash: actualHash,
		};

		// Check if this navigation should be blocked
		const shouldBlockNavigation = shouldBlockRef.current({
			currentLocation: currentLocationWithParams,
			nextLocation: nextLocationWithParams,
		});

		// If we should block and location has changed
		if (shouldBlockNavigation) {
			// Prevent duplicate blocking for the same path
			const pathKey = `${location.pathname}${actualSearch}${actualHash}`;
			if (lastBlockedPathRef.current === pathKey) {
				return;
			}

			lastBlockedPathRef.current = pathKey;

			// Store the attempted location and previous location
			const attemptedLocation = { ...nextLocationWithParams };
			const previousLocation = { ...currentLocationWithParams };

			// Navigate back FIRST to prevent flicker - do this before showing modal
			isNavigatingRef.current = true;
			navigate(previousLocation.pathname + previousLocation.search + previousLocation.hash, {
				replace: true,
				state: previousLocation.state,
			});

			// Set blocked state after navigation - this will trigger the modal on the correct page
			// Use microtask (queueMicrotask) for immediate execution after current task
			queueMicrotask(() => {
				setBlockerState('blocked');
				setBlockedLocation(attemptedLocation);
				isNavigatingRef.current = false;
			});
		} else {
			// Navigation is allowed, update the previous location with actual params
			previousLocationRef.current = nextLocationWithParams;
			currentLocationRef.current = nextLocationWithParams;
		}
	}, [location, blockerState, navigate]);

	return {
		state: blockerState,
		location: blockedLocation,
		proceed,
		reset,
	};
}
